import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTable, useCustom } from '@refinedev/core';
import { StatCard } from '../../components/ui/stat-card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { EditButton, ShowButton, DeleteButton, CreateButton } from '../../components/crud';
import { Building2, Mail, Phone, Globe, Briefcase, CheckCircle2, CalendarPlus } from "lucide-react";
import { PageHeader } from '@/components/ui/page-header';
import { rowOpenProps } from '@/lib/rowOpen';

interface Company {
  id: number;
  code: string;
  name: string;
  description?: string;
  legal_name: string;
  tax_id?: string;
  registration_number?: string;
  email: string;
  phone: string;
  website?: string;
  status: 'active' | 'inactive' | 'suspended';
  is_active: boolean;
  settings: any;
  created_by_name: string;
  updated_by_name: string;
  created_at: string;
  updated_at: string;
  addresses: any[];
  branch_count: number;
}

interface CompanyStats {
  total_companies: number;
  active_companies: number;
  total_branches: number;
  recent_companies: number;
}

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'active': return 'success' as const;
    case 'inactive': return 'secondary' as const;
    case 'suspended': return 'error' as const;
    default: return 'secondary' as const;
  }
};

export const CompanyProfileList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { tableQuery } = useTable<Company>({
    resource: 'companies',
    sorters: {
      initial: [{ field: 'name', order: 'asc' }],
    },
  });

  const statsQuery = useCustom<CompanyStats>({
    url: '/v1/core/companies/statistics/',
    method: 'get',
    errorNotification: false,
    queryOptions: {
      select: (response: any) => {
        try {
          if (response?.data && typeof response.data === 'object') {
            const d = response.data;
            return {
              ...response,
              data: {
                total_companies: Number(d.total_companies) || 0,
                active_companies: Number(d.active_companies) || 0,
                total_branches: Number(d.total_branches) || 0,
                recent_companies: Number(d.recent_companies) || 0,
              },
            };
          }
        } catch (_) { /* swallow */ }
        return { ...response, data: { total_companies: 0, active_companies: 0, total_branches: 0, recent_companies: 0 } };
      },
    },
  });

  const companies: Company[] = (tableQuery.data?.data as any) ?? [];
  const stats: CompanyStats = (statsQuery.data as any)?.data ?? {
    total_companies: 0, active_companies: 0, total_branches: 0, recent_companies: 0,
  };

  const filtered = search
    ? companies.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.code.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase())
      )
    : companies;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Company Profile Management"
        icon={<Building2 className="h-6 w-6" />}
        actions={<CreateButton resource="companies" label="Add Company" />}
      />

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Total Companies" value={stats.total_companies} icon={Briefcase} tone="brand" />
        <StatCard label="Active Companies" value={stats.active_companies} icon={CheckCircle2} tone="success" />
        <StatCard label="Total Branches" value={stats.total_branches} icon={Building2} tone="brand" />
        <StatCard label="Recent Companies" value={stats.recent_companies} icon={CalendarPlus} tone="brand" subtitle="this month" />
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search companies..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-3 text-left font-medium">Company</th>
              <th className="p-3 text-left font-medium">Legal Info</th>
              <th className="p-3 text-left font-medium">Contact</th>
              <th className="p-3 text-left font-medium">Status</th>
              <th className="p-3 text-center font-medium">Branches</th>
              <th className="p-3 text-center font-medium">Addresses</th>
              <th className="p-3 text-left font-medium">Updated</th>
              <th className="p-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No companies found</td></tr>
            ) : (
              filtered.map((record) => (
                <tr key={record.id} {...rowOpenProps(() => navigate(`/core/companies/edit/${record.id}`), 'border-b hover:bg-muted/50 transition-colors')}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-info-100 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-info" />
                      </div>
                      <div>
                        <p className="font-medium">{record.name}</p>
                        <p className="text-xs text-muted-foreground">Code: {record.code}</p>
                        {record.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {record.description.length > 60 ? `${record.description.substring(0, 60)}...` : record.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <p className="font-medium text-sm">{record.legal_name}</p>
                    {record.tax_id && <p className="text-xs text-muted-foreground">Tax ID: {record.tax_id}</p>}
                    {record.registration_number && <p className="text-xs text-muted-foreground">Reg: {record.registration_number}</p>}
                  </td>
                  <td className="p-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-xs">
                        <Mail className="h-3 w-3 text-info" />
                        <span>{record.email}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs">
                        <Phone className="h-3 w-3 text-success-700" />
                        <span>{record.phone}</span>
                      </div>
                      {record.website && (
                        <div className="flex items-center gap-1.5 text-xs">
                          <Globe className="h-3 w-3 text-info-700" />
                          <span>{record.website}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant={getStatusVariant(record.status)}>
                      {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {record.is_active ? 'Active' : 'Inactive'}
                    </p>
                  </td>
                  <td className="p-3 text-center">
                    <p className="text-lg font-bold text-info">{record.branch_count}</p>
                    <p className="text-xs text-muted-foreground">{record.branch_count === 1 ? 'branch' : 'branches'}</p>
                  </td>
                  <td className="p-3 text-center">
                    <p className="text-lg font-bold text-success-700">{Array.isArray(record.addresses) ? record.addresses.length : 0}</p>
                    <p className="text-xs text-muted-foreground">
                      {(Array.isArray(record.addresses) ? record.addresses.length : 0) === 1 ? 'address' : 'addresses'}
                    </p>
                  </td>
                  <td className="p-3">
                    <p className="text-xs">{new Date(record.updated_at).toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">by {record.updated_by_name || record.created_by_name}</p>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <ShowButton hideText size="sm" recordItemId={record.id} resource="companies" />
                      <EditButton hideText size="sm" recordItemId={record.id} resource="companies" />
                      <DeleteButton hideText size="sm" recordItemId={record.id} resource="companies" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyProfileList;
